// Ball flight and roll on a hole. Deterministic: the same hole, the same input and the same wind
// seed give the same samples on the Worker and in the browser. The Worker's result is the one that
// counts; the browser uses it for playback and this module for prediction and for the bot's
// candidate search. Fixed step, no randomness except the seeded wind.

import { gradient, sampleHeight, sampleSurface, type Hole, type Surface } from './course';
import { mulberry32, seedFrom } from './rng';

export type Club = 'driver' | 'wood' | 'iron5' | 'iron7' | 'iron9' | 'wedge' | 'putter';
export const CLUBS: Record<Club, { loftDeg: number; speed: number; label: string }> = {
  driver: { loftDeg: 11, speed: 70, label: 'Driver' },
  wood: { loftDeg: 16, speed: 62, label: '3 Wood' },
  iron5: { loftDeg: 24, speed: 54, label: '5 Iron' },
  iron7: { loftDeg: 32, speed: 50, label: '7 Iron' },
  iron9: { loftDeg: 40, speed: 46, label: '9 Iron' },
  wedge: { loftDeg: 52, speed: 40, label: 'Wedge' },
  putter: { loftDeg: 3, speed: 7, label: 'Putter' },
};
export const CLUB_ORDER: Club[] = ['driver', 'wood', 'iron5', 'iron7', 'iron9', 'wedge', 'putter'];

export interface ShotInput {
  club: Club;
  /** Compass-like aim in degrees: 0 points along +z (toward the far end), positive turns toward +x. */
  aimDeg: number;
  /** 0..1 */
  power: number;
  /** -1..1, positive is backspin. */
  spin: number;
  from: { x: number; z: number };
  /** Seed for this shot's wind, usually seedFrom(hole.seed, strokeIndex). */
  windSeed: number;
}

export interface ShotSample { t: number; x: number; y: number; z: number }

export interface ShotResult {
  samples: ShotSample[];
  final: { x: number; z: number };
  surface: Surface;
  carryM: number;
  totalM: number;
  events: string[];
  holed: boolean;
  penalty: number;
  wind: { x: number; z: number };
  distanceToCupM: number;
}

export const CUP_RADIUS_M = 0.054;
const DT = 1 / 120;
const SAMPLE_EVERY = 4;
const MAX_T = 25;
const G = 9.81;
// per-metre aero coefficients for a golf ball (0.5 * rho * C * A / m with rho 1.2, A 1.43e-3, m 45.9 g)
const K_DRAG = 0.0047;   // C_d ~ 0.25
const K_LIFT = 0.0033;   // C_l ~ 0.18 at neutral spin; the spin control scales it

// the turf: normal restitution, the fraction of tangential speed that survives an impact, and
// rolling resistance in m/s^2. Calibrated with scripts/calibrate.ts, not by eye.
const RESTITUTION: Record<Surface, number> = { tee: 0.35, fairway: 0.35, green: 0.3, rough: 0.25, sand: 0.05, water: 0, oob: 0.3 };
const TURF_GRIP: Record<Surface, number> = { tee: 0.5, fairway: 0.5, green: 0.5, rough: 0.35, sand: 0.15, water: 0, oob: 0.4 };
// green 0.65 → 1.0 on 2026-09-13: a stimp-10 green, so a putt stops on a slope rather than wandering
// off it (the old value was slower than any real green and, with the old contours, unputtable).
export const ROLL_DECEL: Record<Surface, number> = { tee: 2.2, fairway: 2.2, green: 1.0, rough: 5.0, sand: 9.0, water: 99, oob: 3.0 };

/** The putter power that rolls out to `distM` on a flat green; the client scales its meter to it. */
export function puttPowerFor(distM: number): number {
  const speed = Math.sqrt(2 * ROLL_DECEL.green * Math.max(0.3, distM)) + 0.25;
  return Math.max(0.03, Math.min(1, speed / CLUBS.putter.speed));
}

export function windFor(windSeed: number): { x: number; z: number } {
  const r = mulberry32(windSeed);
  const speed = r() * 6;
  const dir = r() * Math.PI * 2;
  return { x: Math.cos(dir) * speed, z: Math.sin(dir) * speed };
}

export function windSeedFor(hole: Hole, strokeIndex: number): number {
  return seedFrom(hole.seed, 'wind', strokeIndex);
}

export function aimTo(from: { x: number; z: number }, to: { x: number; z: number }): number {
  return (Math.atan2(to.x - from.x, to.z - from.z) * 180) / Math.PI;
}

export function simulateShot(hole: Hole, input: ShotInput): ShotResult {
  const club = CLUBS[input.club];
  const power = Math.max(0, Math.min(1, input.power));
  const spin = Math.max(-1, Math.min(1, input.spin));
  // a full swing has a floor (a chip is still a swing); a putt is linear, so a one-metre tap exists
  const speed = input.club === 'putter' ? club.speed * Math.max(0.03, power) : club.speed * (0.3 + 0.7 * power);
  const aim = (input.aimDeg * Math.PI) / 180;
  const loft = (club.loftDeg * Math.PI) / 180;
  const wind = windFor(input.windSeed);
  const events: string[] = [];
  const samples: ShotSample[] = [];

  let x = input.from.x, z = input.from.z;
  let y = sampleHeight(hole, x, z) + 0.02;
  let vx = Math.sin(aim) * Math.cos(loft) * speed;
  let vz = Math.cos(aim) * Math.cos(loft) * speed;
  let vy = Math.sin(loft) * speed;
  const start = { x, z };
  let rolling = input.club === 'putter';
  if (rolling) { vy = 0; vx = Math.sin(aim) * speed; vz = Math.cos(aim) * speed; y = sampleHeight(hole, x, z); }
  let t = 0, step = 0;
  let carry = 0, landed = rolling;
  let lastLand = { x, z };
  let holed = false, penalty = 0;
  let outcome: 'rest' | 'water' | 'oob' | 'holed' | 'timeout' = 'timeout';

  samples.push({ t: 0, x, y, z });

  while (t < MAX_T) {
    if (!rolling) {
      // flight: gravity, quadratic drag against air, Magnus lift from backspin, wind as relative air
      const rx = vx - wind.x, rz = vz - wind.z, ry = vy;
      const rs = Math.hypot(rx, ry, rz) || 1e-6;
      let ax = -K_DRAG * rs * rx, ay = -G - K_DRAG * rs * ry, az = -K_DRAG * rs * rz;
      const ux = rx / rs, uy = ry / rs, uz = rz / rs;
      // lift direction: the component of "up" perpendicular to the velocity
      let lx = -uy * ux, ly = 1 - uy * uy, lz = -uy * uz;
      const ln = Math.hypot(lx, ly, lz) || 1;
      lx /= ln; ly /= ln; lz /= ln;
      const lift = K_LIFT * (0.7 + 0.4 * spin) * rs * rs;
      ax += lift * lx; ay += lift * ly; az += lift * lz;
      vx += ax * DT; vy += ay * DT; vz += az * DT;
      x += vx * DT; y += vy * DT; z += vz * DT;
      const ground = sampleHeight(hole, x, z);
      if (y <= ground) {
        y = ground;
        const s = sampleSurface(hole, x, z);
        if (!landed) { landed = true; carry = Math.hypot(x - start.x, z - start.z); }
        if (s === 'oob') { outcome = 'oob'; events.push('out of bounds'); break; }
        if (s === 'water') { outcome = 'water'; events.push('splash'); break; }
        lastLand = { x, z };
        const g = gradient(hole, x, z);
        let nx = -g.gx, ny = 1, nz = -g.gz;
        const nn = Math.hypot(nx, ny, nz); nx /= nn; ny /= nn; nz /= nn;
        const vn = vx * nx + vy * ny + vz * nz;   // negative on the way in
        if (vn >= 0) { y = ground + 0.001; }
        else {
          // the impact: the normal component rebounds by the restitution, the tangential component is
          // scrubbed by the turf (backspin bites harder, topspin skids on), and a rebound too small to
          // leave the ground becomes a roll
          let tx = vx - vn * nx, ty = vy - vn * ny, tz = vz - vn * nz;
          const grip = TURF_GRIP[s] * (spin > 0 ? 1 - 0.35 * spin : 1 + 0.15 * -spin);
          tx *= grip; ty *= grip; tz *= grip;
          const out = -RESTITUTION[s] * vn;
          vx = tx + out * nx; vy = ty + out * ny; vz = tz + out * nz;
          if (out < 1.0) { rolling = true; vy = 0; }
          events.push(`bounce:${s}`);
        }
      }
    } else {
      // rolling: friction by surface, gravity along the slope, stop when slow
      const s = sampleSurface(hole, x, z);
      if (s === 'oob') { outcome = 'oob'; events.push('out of bounds'); break; }
      if (s === 'water') { outcome = 'water'; events.push('splash'); break; }
      lastLand = { x, z };
      const sp = Math.hypot(vx, vz);
      const g = gradient(hole, x, z);
      let ax = -G * g.gx, az = -G * g.gz;
      if (sp > 1e-4) { const dec = ROLL_DECEL[s]; ax -= (vx / sp) * dec; az -= (vz / sp) * dec; }
      vx += ax * DT; vz += az * DT;
      const sp2 = Math.hypot(vx, vz);
      if (sp2 < 0.05 && sp < 0.3) { vx = 0; vz = 0; outcome = 'rest'; break; }
      x += vx * DT; z += vz * DT; y = sampleHeight(hole, x, z);
      const dCup = Math.hypot(x - hole.cup.x, z - hole.cup.z);
      if (dCup < CUP_RADIUS_M * 2 && sp2 < 1.6 && s === 'green') { holed = true; outcome = 'holed'; x = hole.cup.x; z = hole.cup.z; events.push('holed'); break; }
    }
    t += DT; step++;
    if (step % SAMPLE_EVERY === 0) samples.push({ t, x, y, z });
  }
  if (outcome === 'timeout') outcome = 'rest';

  let final = { x, z };
  if (outcome === 'water') { penalty = 1; final = lastLand; }
  if (outcome === 'oob') { penalty = 1; final = start; }
  if (!holed) { final.x = Math.max(-hole.widthM / 2 + 1, Math.min(hole.widthM / 2 - 1, final.x)); final.z = Math.max(1, Math.min(hole.lengthM - 1, final.z)); }
  samples.push({ t, x, y, z });
  const surface = holed ? 'green' : sampleSurface(hole, final.x, final.z);
  return {
    samples,
    final,
    surface,
    carryM: Math.round(carry * 10) / 10,
    totalM: Math.round(Math.hypot(final.x - start.x, final.z - start.z) * 10) / 10,
    events,
    holed,
    penalty,
    wind,
    distanceToCupM: holed ? 0 : Math.round(Math.hypot(final.x - hole.cup.x, final.z - hole.cup.z) * 10) / 10,
  };
}

export interface Candidate {
  club: Club;
  aimDeg: number;
  power: number;
  spin: number;
  predictedDistanceM: number;
  predictedSurface: Surface;
  predictedHoled: boolean;
  penalty: number;
  score: number;
}

/** A deterministic search over clubs, powers and aim offsets: the best-predicted shots from here.
 *  The AI chooses among these; it never invents a shot the physics has not already evaluated. */
export function planShots(hole: Hole, from: { x: number; z: number }, windSeed: number, count = 5): Candidate[] {
  const base = aimTo(from, hole.cup);
  const onGreen = sampleSurface(hole, from.x, from.z) === 'green';
  const dCup = Math.hypot(from.x - hole.cup.x, from.z - hole.cup.z);
  const clubs: Club[] = onGreen || dCup < 25 ? ['putter', 'wedge', 'iron9'] : CLUB_ORDER;
  const swingPowers = [0.35, 0.5, 0.65, 0.8, 0.92, 1.0];
  // a putt's power comes from the distance: the speed that just reaches the cup on a flat green,
  // then a spread around it for slope and for the firmness that drops it rather than lipping out
  const puttSpeed = Math.sqrt(2 * ROLL_DECEL.green * Math.max(0.3, dCup)) + 0.25;
  const puttPowers = [0.75, 0.9, 1.0, 1.1, 1.25, 1.45].map((m) => Math.max(0.03, Math.min(1, (puttSpeed * m) / CLUBS.putter.speed)));
  // a green may slope 3 % now, and 3 % across an 8 m putt is six degrees of break: the planner
  // reads out to ±7° so Dex and the advice can hole what a person can (QC round 2)
  const aims = onGreen ? [-7, -4.5, -2.5, -1.2, 0, 1.2, 2.5, 4.5, 7] : [-5, -2, 0, 2, 5];
  const out: Candidate[] = [];
  for (const club of clubs) for (const power of club === 'putter' ? puttPowers : swingPowers) for (const da of aims) {
    const r = simulateShot(hole, { club, aimDeg: base + da, power, spin: 0, from, windSeed });
    const score = r.holed ? -1000 : r.distanceToCupM + r.penalty * 60 + (r.surface === 'sand' ? 15 : r.surface === 'rough' ? 6 : 0);
    out.push({ club, aimDeg: base + da, power, spin: 0, predictedDistanceM: r.distanceToCupM, predictedSurface: r.surface, predictedHoled: r.holed, penalty: r.penalty, score });
  }
  out.sort((a, b) => a.score - b.score);
  return out.slice(0, count);
}
